import React, { useEffect, useState } from 'react';
import { AnalyticalTable, Button, ObjectStatus, Dialog, Title, Select, Option, BusyIndicator, DatePicker, Input, Icon, FlexBox, FlexBoxJustifyContent, MessageStrip, Text } from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { parseToNumber, formatNumberWithComma, rightAlignedNumberStyle, getStatusStyle, getStatusComponentProps } from '../utils/validator';
import usePermission from '../hooks/usePermission';
import useAutoTranslate from '../hooks/useAutoTranslate'; // Importar el hook de traducción

const ASNList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    sortBy: 'asnDate',
    sortDirection: 'desc'
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDraftView, setIsDraftView] = useState(false); // Nuevo estado para controlar la vista de borradores
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate(); // Usar el hook de traducción
  
  // Estados para la búsqueda avanzada
  const [searchParams, setSearchParams] = useState({
    asnNumber: '',
    purchaseOrderNumber: '',
    startDate: null,
    endDate: null,
    asnStatus: ''
  });
  
  // Estado para errores de validación
  const [dateError, setDateError] = useState('');
  
  // Estado para controlar si se está buscando
  const [isSearching, setIsSearching] = useState(false);
  
  // Estado para controlar si debemos ejecutar la búsqueda
  const [shouldSearch, setShouldSearch] = useState(false);

  useEffect(() => {
    if (!isSearching) {
      fetchData(currentPage);
    }
  }, [currentPage, isSearching, isDraftView]); // Agregamos isDraftView como dependencia

  // Efecto modificado para ejecutar búsqueda solo cuando ambas sean necesarias
  useEffect(() => {
    if (shouldSearch && !dateError) {
      const debounceTimeout = setTimeout(() => {
        handleAdvancedSearch();
        setShouldSearch(false);
      }, 500); // Debounce de 500ms para evitar demasiadas peticiones
      
      return () => clearTimeout(debounceTimeout);
    }
  }, [shouldSearch, dateError]);

  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      // Usar endpoint diferente según si estamos en vista de borradores o no
      const baseUrl = isDraftView ? 'asn-documents/draft' : 'asn-documents';
      const url = `${baseUrl}?page=${page}&size=20`;
      const response = await axiosInstance.get(url);

      if (response.data.records.code === "SUCCESS") {
        setData(response.data.records.data);
        console.log('Datos recibidos:', response.data.records.data);
        if (isDraftView && response.data.records.data.length > 0) {
        }
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError(autoT('Error fetching data'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdvancedSearch = async () => {
    try {
      console.log('=== handleAdvancedSearch ===');
      console.log('Estado actual de searchParams:', searchParams);
      console.log('Estado actual de isSearching:', isSearching);
      
      setLoading(true);
      
      // Construir parámetros de búsqueda
      const params = new URLSearchParams();
      
      if (searchParams.asnNumber) {
        params.append('asnNumber', searchParams.asnNumber.trim());
      }
      
      if (searchParams.purchaseOrderNumber) {
        params.append('purchaseOrderNumber', searchParams.purchaseOrderNumber.trim());
      }
      
      if (searchParams.asnStatus) {
        params.append('asnStatus', searchParams.asnStatus);
        console.log('Agregando estado a la búsqueda:', searchParams.asnStatus);
      }
      
      // Verificar si hay al menos un parámetro de búsqueda
      if (params.toString()) {
        setIsSearching(true);
        // Usar endpoint diferente según si estamos en vista de borradores o no
        const baseUrl = isDraftView ? 'asn-documents/draft/search' : 'asn-documents/search';
        const url = `${baseUrl}?${params.toString()}`;
        console.log("URL de búsqueda:", url);
        const response = await axiosInstance.get(url);
        
        if (response.data.records.code === "SUCCESS") {
          console.log('Búsqueda exitosa');
          console.log('Estado actual antes de actualizar datos:', searchParams.asnStatus);
          setData(response.data.records.data);
          // Mantener el estado actual de searchParams
          const currentStatus = searchParams.asnStatus;
          console.log('Manteniendo estado actual:', currentStatus);
          setSearchParams(prev => {
            const newParams = {
              ...prev,
              asnStatus: currentStatus
            };
            console.log('Nuevos searchParams después de la búsqueda:', newParams);
            return newParams;
          });
        } else {
          setError(response.data.records.message);
        }
      } else {
        setIsSearching(false);
        fetchData(currentPage);
      }
    } catch (err) {
      console.error("Error en búsqueda avanzada:", err);
      setError(autoT('Error en la búsqueda avanzada'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (field, value) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: typeof value === 'string' ? value.trim() : value
    }));
    
    // Solo ejecutamos la búsqueda inmediatamente para campos que no son fechas
    if (field !== 'startDate' && field !== 'endDate') {
      setShouldSearch(true);
    }
  };

  const handleStartDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        // Si se limpia la fecha
        setSearchParams((prev) => ({
          ...prev,
          startDate: null
        }));
        setDateError('');
        return;
      }
      
      // Parsear fecha en formato dd/MM/yyyy
      let newStartDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newStartDate = new Date(`${year}-${month}-${day}T00:00:00`);
        } else {
          newStartDate = new Date(dateValue);
        }
      } catch (error) {
        newStartDate = new Date(dateValue);
      }
      
      // Validar que la fecha sea válida
      if (isNaN(newStartDate.getTime())) {
        console.error("Fecha de inicio inválida:", dateValue);
        setDateError(autoT('Fecha de inicio inválida'));
        return;
      }
      
      // Validación: Si hay fecha de fin, verificar que la fecha de inicio no sea posterior
      if (searchParams.endDate) {
        const endDate = new Date(searchParams.endDate);
        if (newStartDate > endDate) {
          setDateError(autoT('La fecha de inicio no puede ser posterior a la fecha de fin'));
          return;
        }
      }
      
      setDateError('');
      setSearchParams((prev) => ({
        ...prev,
        startDate: newStartDate
      }));
      
      // Solo activamos la búsqueda si ya tenemos ambas fechas
      if (searchParams.endDate) {
        setShouldSearch(true);
      }
    } catch (error) {
      console.error("Error al procesar fecha de inicio:", error);
      setDateError(autoT('Error al procesar la fecha de inicio'));
    }
  };

  const handleEndDateChange = (event) => {
    try {
      const dateValue = event.detail.value;
      if (!dateValue) {
        // Si se limpia la fecha
        setSearchParams((prev) => ({
          ...prev,
          endDate: null
        }));
        setDateError('');
        return;
      }
      
      // Parsear fecha en formato dd/MM/yyyy
      let newEndDate;
      try {
        const [day, month, year] = dateValue.split('/');
        if (day && month && year) {
          newEndDate = new Date(`${year}-${month}-${day}T23:59:59`);
        } else {
          newEndDate = new Date(dateValue);
        }
      } catch (error) {
        newEndDate = new Date(dateValue);
      }
      
      // Validar que la fecha sea válida
      if (isNaN(newEndDate.getTime())) {
        console.error("Fecha de fin inválida:", dateValue);
        setDateError(autoT('Fecha de fin inválida'));
        return;
      }
      
      // Validación: Si hay fecha de inicio, verificar que la fecha de fin no sea anterior
      if (searchParams.startDate) {
        const startDate = new Date(searchParams.startDate);
        if (newEndDate < startDate) {
          setDateError(autoT('La fecha de fin no puede ser anterior a la fecha de inicio'));
          return;
        }
      }
      
      setDateError('');
      setSearchParams((prev) => ({
        ...prev,
        endDate: newEndDate
      }));
      
      // Solo activamos la búsqueda si ya tenemos ambas fechas
      if (searchParams.startDate) {
        setShouldSearch(true);
      }
    } catch (error) {
      console.error("Error al procesar fecha de fin:", error);
      setDateError(autoT('Error al procesar la fecha de fin'));
    }
  };

  const handleAsnStatusChange = (event) => {
    const newStatus = event.detail.selectedOption.value;
    console.log('=== handleAsnStatusChange ===');
    console.log('Nuevo estado seleccionado:', newStatus);
    console.log('Estado actual de searchParams:', searchParams);
    
    // Actualizar el estado de búsqueda
    setSearchParams(prev => {
      const updatedParams = {
        ...prev,
        asnStatus: newStatus
      };
      console.log('Actualizando searchParams con:', updatedParams);
      return updatedParams;
    });
    
    // Activar la búsqueda
    setShouldSearch(true);
    console.log('Activando búsqueda (shouldSearch = true)');
  };

  const handleResetSearch = () => {
    setSearchParams({
      asnNumber: '',
      purchaseOrderNumber: '',
      startDate: null,
      endDate: null,
      asnStatus: ''
    });
    setDateError('');
    setIsSearching(false); // Desactivar modo búsqueda
    fetchData(1);
    setCurrentPage(1);
  };

  const handleToggleDraftView = () => {
    setIsDraftView(!isDraftView);
    setCurrentPage(1); // Resetear a la primera página
    setSearchParams({
      asnNumber: '',
      purchaseOrderNumber: '',
      startDate: null,
      endDate: null,
      asnStatus: ''
    });
    setDateError('');
    setIsSearching(false);
  };

  const handlePrint = async (row) => {
    if (!row || !row.id) {
      console.error(autoT('No ASN ID found for printing'));
      return;
    }
    
    // Verificar si el usuario tiene permisos para imprimir notificaciones
    if (!hasPermission('notification', 'listView')) {
      console.error(autoT('Usuario sin permisos para imprimir notificaciones'));
      return;
    }
    
    try {
      console.log(autoT('Verificando existencia del PDF para ASN:'), row.asnNumber);
      
      // Obtener el token del localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error(autoT('No se encontró el token de autenticación'));
      }

      // Construir la URL base correctamente
      const baseUrl = process.env.REACT_APP_API_URL || 'https://localhost:8443/api/v1';

      // Primero verificar si el PDF existe
      const verifyUrl = `${baseUrl}/blob/asn-documents/verify/${row.asnNumber}`.replace(/([^:]\/)\/+/g, "$1");
      
      console.log(autoT('Verificando PDF en:'), verifyUrl);
      
      const verifyResponse = await fetch(verifyUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Origin': window.location.origin
        },
        credentials: 'include',
        mode: 'cors'
      });

      if (!verifyResponse.ok) {
        throw new Error(autoT('Error al verificar el PDF:') + ` ${verifyResponse.status} ${verifyResponse.statusText}`);
      }

      // Proceder con la descarga
      const downloadUrl = `${baseUrl}/blob/asn-documents/download/${row.asnNumber}`.replace(/([^:]\/)\/+/g, "$1");
      
      console.log(autoT('Descargando PDF desde:'), downloadUrl);
      
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf',
          'Origin': window.location.origin
        },
        credentials: 'include',
        mode: 'cors'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(autoT('Error de autenticación. Por favor, inicie sesión nuevamente.'));
        }
        throw new Error(autoT('Error al descargar el PDF:') + ` ${response.status} ${response.statusText}`);
      }

      // Verificar el tipo de contenido
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/pdf')) {
        console.warn(autoT('Tipo de contenido inesperado:'), contentType);
      }

      // Obtener el blob del PDF
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error(autoT('El PDF descargado está vacío'));
      }

      // Crear URL del blob y descargar
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `ASN-${row.asnNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Limpieza
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);

      console.log(autoT('PDF descargado exitosamente para ASN:'), row.asnNumber);
    } catch (error) {
      console.error(autoT('Error al descargar el PDF:'), error);
      if (error.response) {
        try {
          const errorText = await error.response.text();
          console.error(autoT('Estado de la respuesta:'), error.response.status);
          console.error(autoT('Detalles del error:'), errorText);
        } catch (e) {
          console.error(autoT('No se pudieron obtener detalles adicionales del error'));
        }
      }
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRow(null);
  };

  const handleConfirmPrint = async (packageData) => {
    if (!packageData || !packageData.packageNumber) {
      console.error(autoT('No package number found for printing'));
      return;
    }
  
    // Verificar si el usuario tiene permisos para imprimir notificaciones
    if (!hasPermission('notification', 'listView')) {
      console.error(autoT('Usuario sin permisos para imprimir paquetes'));
      return;
    }
    
    try {
      // Fetch the PDF URL using the package data
      console.log(packageData.packingLists)
      const response = await axiosInstance.get(
        `packages/getPdf/${packageData.packingLists[0].asnPackageId}`
      );
  
      const pdfUrl = response.data.packageNumber; // Adjust this based on the actual response structure
      if (!pdfUrl) {
        console.error(autoT('No PDF URL returned from the server'));
        return;
      }
  
      // Download the PDF
      const pdfResponse = await axiosInstance.get(pdfUrl, {
        responseType: 'blob', // Ensure the response is handled as a binary blob
      });
  
      // Create a URL for the blob and trigger a download
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const downloadUrl = URL.createObjectURL(blob);
  
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${packageData.packageNumber}.pdf`; // Use the package number for naming the file
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
  
      console.log(autoT('PDF downloaded for package:'), packageData.packageNumber);
    } catch (error) {
      console.error(autoT('Error fetching or downloading the PDF:'), error.message);
    } finally {
      handleCloseDialog();
    }
  };
  
  useEffect(() => {
    console.log('ASNList – data original antes de ordenar:', data);

    const sortedData = [...data].sort((a, b) =>
      new Date(b.asnDate) - new Date(a.asnDate)
    );

    console.log('ASNList – sortedData después de ordenar:', sortedData);

    setFilteredData(sortedData);
  }, [data]);
  

  const asnStatusOptions = [
    { value: '', label: autoT('Todos') },
    { value: 'Draft', label: autoT('Borrador') },
    { value: 'Approved', label: autoT('Finalizado') },
    { value: 'Shipped', label: autoT('Despachada') },
    { value: 'Pending', label: autoT('Creada') },
    { value: 'Default', label: autoT('Por Despachar') },
    { value: 'Received', label: autoT('Recepcionada') },
    { value: 'Canceled', label: autoT('Anulada') },
    { value: 'Shipment', label: autoT('Preparando envio') },
    { value: 'In transit', label: autoT('En camino') },
    { value: 'Delivered', label: autoT('Entregado') },
    { value: 'Returned', label: autoT('Devuelto') },
    { value: 'Modified', label: autoT('Modificado') }
  ];

  const getAsnStatusLabel = (statusValue) => {
    const status = asnStatusOptions.find(opt => opt.value === statusValue);
    return status ? status.label : autoT('Creada'); // Por defecto retorna 'Creada'
  };

  const handleSort = (event) => {
    console.log('handleSort called with event:', event);
    const { column, sortBy } = event.detail;
    console.log('Sort details:', { column, sortBy });

    if (!column || !sortBy) {
      console.log('No column or sortBy provided, returning');
      return;
    }

    const sortedData = [...data].sort((a, b) => {
      console.log('Comparing values:', {
        a: a[column.accessor],
        b: b[column.accessor],
        sortBy
      });

      if (sortBy === 'asc') {
        return a[column.accessor] > b[column.accessor] ? 1 : -1;
      } else {
        return a[column.accessor] < b[column.accessor] ? 1 : -1;
      }
    });

    console.log('Sorted data:', sortedData);
    setData(sortedData);
  };

  const columns = [
    { 
      Header: autoT('Número'), 
      accessor: 'asnNumber',
      sortable: true,
      hAlign: 'center'
    },
    { 
      Header: autoT('Fecha Salida'), 
      accessor: 'asnDate',
      sortable: true,
      disableFilters: true,
      hAlign: 'center',
      Cell: ({ value }) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
    },
    { 
      Header: autoT('Fecha Entrega'), 
      accessor: 'deliveryDate',
      sortable: true,
      hAlign: 'center',
      Cell: ({ value }) => {
        if (!value) return '';

        // Ignorar completamente zona horaria: usar solo la parte de fecha
        const isoDate = String(value).split('T')[0]; // yyyy-MM-dd
        const [year, month, day] = isoDate.split('-');

        if (!year || !month || !day) {
          return '';
        }

        // Formatear manualmente dd/MM/yyyy sin crear objeto Date
        return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
      },
      disableFilters: true,
    },
    { 
      Header: autoT('Tipo de bulto'), 
      accessor: 'shipmentType',
      sortable: true,
      hAlign: 'center'
    },
    { 
      Header: autoT('Medio de transporte'), 
      accessor: 'transportMedium',
      sortable: true,
      hAlign: 'center'
    },
    {
      Header: autoT('Estado'),
      accessor: 'asnStatus',
      sortable: true,
      hAlign: 'center',
      Cell: ({ row }) => {
        const status = row.original.asnStatus;
        // Usamos la función getStatusComponentProps para obtener los estilos
        const statusProps = getStatusComponentProps(status);

        return (
          <ObjectStatus
            style={statusProps.style}
          >
            {getAsnStatusLabel(status)}
          </ObjectStatus>
        );
      },
    },
    { 
      Header: autoT('Tipo de carga'), 
      accessor: 'chargeType',
      sortable: true,
      width: 150,
      hAlign: 'center'
    },
    {
      Header: autoT('N° OC'),
      accessor: 'purchaseOrderNumber',
      sortable: true,
      width: 150,
      hAlign: 'center'
    },
    {
      Header: autoT('Imprimir Notif.'),
      id: 'actions',
      sortable: false,
      width: 150,
      hAlign: 'center',
      vAlign: 'center',
      Cell: ({ row }) => (
        (row.original.asnStatus !== 'Pending' && row.original.asnStatus !== 'Modified') && 
        hasPermission('notification', 'listView') && (
          <Button
            icon="print"
            design="Transparent"
            style={{ color: 'grey' }}
            onClick={() => handlePrint(row.original)}
            title={autoT('Imprimir')}
          />
        )
      ),
    }
  ];

  const handleRowClick = (items) => {
    console.log('=== handleRowClick llamado ===');
    console.log('Items recibidos:', items);
    console.log('isDraftView:', isDraftView);
    
    // Si no hay items, salir
    if (!items || items.length === 0) {
      console.log('No hay items para procesar');
      return;
    }
    
    items.forEach((item, index) => {
      console.log(`Item ${index}:`, item);
      console.log(`ID: ${item.id}, ASN Document ID: ${item.asnDocumentId}`);
      
      // Verificar si el usuario tiene permisos para ver detalles de notificaciones
      if (hasPermission('notification', 'listView')) {
        // Si estamos en vista de borradores, navegar a ASNDraft
        if (isDraftView) {
          console.log('Navegando a ASNDraft con ID:', item.id);
          navigate(`/asn-draft/${item.id}`);
        } else {
          // Si estamos en vista de notificaciones, navegar a ASNDetail
          console.log('Navegando a ASNDetail con ID:', item.asnDocumentId);
          navigate(`/asn/${item.asnDocumentId}`);
        }
      } else {
        console.log('Usuario sin permisos para ver detalles');
      }
    });
  };

  const handleNextPage = () => {
    setCurrentPage(prevPage => prevPage + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage(prevPage => Math.max(prevPage - 1, 1));
  };

  // Función auxiliar para formatear fechas de manera segura
  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      
      // Formato dd/MM/yyyy
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error al formatear fecha:", error);
      return '';
    }
  };

  if (loading) return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh' 
    }}>
      <BusyIndicator active="true" size="L" text={isDraftView ? autoT("Buscando Borradores...") : autoT("Buscando Notificaciones...")} />
    </div>
  );
  if (error) return <div>Error: {error}</div>;

  return (
    <div style={{ width: '100%', padding: '10px', overflow: 'visible', position: 'relative', paddingBottom: '80px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <Title level="H2">{isDraftView ? autoT("Listado de borradores") : autoT("Listado de notificaciones")}</Title>

        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            design={isDraftView ? "Emphasized" : "Transparent"}
            onClick={handleToggleDraftView}
            icon={isDraftView ? "document" : "document-text"}
          >
            {isDraftView ? autoT("Ver Notificaciones") : autoT("Borradores")}
          </Button>
        </div>
      </div>
      
      {/* Búsqueda avanzada siempre visible */}
      <div style={{ 
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '15px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Número Notificación')}
            </label>
            <Input 
              value={searchParams.asnNumber}
              onChange={(e) => handleSearchInputChange('asnNumber', e.target.value)}
              placeholder={autoT("Ingrese número notificación")}
              style={{ width: '100%' }}
            />
          </div>
          
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Número OC')}
            </label>
            <Input 
              value={searchParams.purchaseOrderNumber}
              onChange={(e) => handleSearchInputChange('purchaseOrderNumber', e.target.value)}
              placeholder={autoT("Ingrese número OC")}
              style={{ width: '100%' }}
            />
          </div>
          
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Estado de la Notificación')}
            </label>
            <Select
              style={{ width: '100%' }}
              onChange={handleAsnStatusChange}
              value={searchParams.asnStatus}
              selectedOption={searchParams.asnStatus}
              selectedIndex={asnStatusOptions.findIndex(opt => opt.value === searchParams.asnStatus)}
            >
              {asnStatusOptions.map((option) => (
                <Option 
                  key={option.value} 
                  value={option.value}
                  selected={option.value === searchParams.asnStatus}
                >
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
        
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr auto',
          gap: '10px',
          alignItems: 'flex-end'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Fecha Inicio')}
            </label>
            <DatePicker 
              value={searchParams.startDate ? formatDate(searchParams.startDate) : ''}
              onChange={handleStartDateChange}
              style={{ width: '100%' }}
              formatPattern="dd/MM/yyyy"
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              {autoT('Fecha fin')}
            </label>
            <DatePicker 
              value={searchParams.endDate ? formatDate(searchParams.endDate) : ''}
              onChange={handleEndDateChange}
              style={{ width: '100%' }}
              formatPattern="dd/MM/yyyy"
            />
          </div>
          
          <Button 
            design="Transparent" 
            onClick={handleResetSearch}
          >
            {autoT('Limpiar filtros')}
          </Button>
        </div>
        
        {dateError && (
          <MessageStrip 
            design="Negative" 
            style={{ marginTop: '10px' }}
          >
            {dateError}
          </MessageStrip>
        )}
        
        {isSearching && (
          <MessageStrip 
            design="Information" 
            style={{ marginTop: '10px' }}
          >
            {autoT('Mostrando resultados filtrados de')} {isDraftView ? autoT("borradores") : autoT("notificaciones")}
          </MessageStrip>
        )}
        
        {searchParams.startDate && !searchParams.endDate && (
          <MessageStrip 
            design="Warning" 
            style={{ marginTop: '10px' }}
          >
            {autoT('Se requiere fecha de fin para buscar por fechas')}
          </MessageStrip>
        )}
        
        {!searchParams.startDate && searchParams.endDate && (
          <MessageStrip 
            design="Warning" 
            style={{ marginTop: '10px' }}
          >
            {autoT('Se requiere fecha de inicio para buscar por fechas')}
          </MessageStrip>
        )}
      </div>
      
      <AnalyticalTable
        data={filteredData}
        columns={columns}
        minRows={filteredData.length > 0 ? filteredData.length : 5}
        visibleRows={filteredData.length > 0 ? filteredData.length : 5}
        style={{
          width: '100%', // Ancho al 100% para que tome todo el espacio disponible
          '--sapList_HeaderBackground': '#f5f6f7',
          '--sapList_BorderColor': '#e5e5e5'
        }}
        onRowClick={(event) => {
          console.log('=== Evento onRowClick ===');
          console.log('Evento completo:', event);
          console.log('Event detail:', event.detail);
          console.log('isDraftView:', isDraftView);
          
          let items = null;
          
          // Si estamos en vista de borradores, usar directamente el objeto de la fila
          if (isDraftView) {
            if (event.detail && event.detail.row && event.detail.row.original) {
              items = [event.detail.row.original];
              console.log('Usando objeto directo del borrador:', items[0]);
            } else {
              console.error('No se pudo extraer datos del borrador');
              return;
            }
          } else {
            // Para notificaciones normales, buscar items
            if (event.detail && event.detail.row && event.detail.row.original && event.detail.row.original.items) {
              items = event.detail.row.original.items;
            } else if (event.detail && event.detail.row && event.detail.row.original) {
              items = [event.detail.row.original];
            } else if (event.detail && event.detail.item) {
              items = [event.detail.item];
            } else {
              console.error('No se pudo extraer datos del evento');
              return;
            }
          }
          
          console.log('Items extraídos:', items);
          handleRowClick(items);
        }}
        onSort={handleSort}
        sortable={true}
        header
        alternateRowColor
        filterable={true}
        scaleWidthMode="Grow" // Para que se expanda y ocupe el espacio disponible
      />

      {dialogOpen && selectedRow && (
        <Dialog
          open={dialogOpen}
          onAfterClose={handleCloseDialog}
          headerText={`${autoT('Detalles de notificación:')} ${selectedRow.asnNumber || ''}`}
        >
          <div style={{ padding: '20px' }}>
            {/* Si la fila seleccionada tiene una propiedad 'package', mostramos esa tabla */}
            {selectedRow.packages && (
              <AnalyticalTable
                data={selectedRow.packages} // Usamos directamente los datos de 'package'
                columns={[
                  {
                    Header: autoT('Package Number'),
                    accessor: 'packageNumber',
                  },
                  {
                    Header: autoT('Weight'),
                    accessor: 'weight',
                    Cell: ({ value }) => (
                      <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
                    )
                  },
                  {
                    Header: autoT('Notificación Document ID'),
                    accessor: 'asnDocumentId',
                  },
                  {
                    Header: autoT('Volume'),
                    accessor: 'volume',
                    Cell: ({ value }) => (
                      <Text style={rightAlignedNumberStyle()}>{formatNumberWithComma(value)}</Text>
                    )
                  },
                  {
                    Header: autoT('Dimensions'),
                    accessor: 'dimensions',
                  },
                  {
                    Header: '',
                    id: 'actions',
                    Cell: ({ row }) => (
                      hasPermission('notification', 'listView') && (
                        <Button
                          icon="print"
                          design="Transparent"
                          style={{ color: 'grey' }}
                          onClick={() => handleConfirmPrint(row.original)}
                          title={autoT('Imprimir')}
                        />
                      )
                    ),
                  }
                ]}
                minRows={1}
                style={{ width: '100%' }}
              />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '10px' }}>
            <Button design="Transparent" onClick={handleCloseDialog}>
              {autoT('Cancelar')}
            </Button>
          </div>
        </Dialog>
      )}
      <div style={{ 
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
        borderTop: '1px solid #e5e5e5',
        zIndex: 100,
        width: '100%'
      }}>
        <Button onClick={handlePreviousPage} disabled={currentPage === 1}>← {autoT('Anterior')}</Button>
        <span style={{ flex: 1, textAlign: 'center' }}>{autoT('Página')} {currentPage}</span>
        <Button onClick={handleNextPage} style={{ marginRight: '2rem' }}>{autoT('Siguiente')} →</Button>
      </div>
    </div>
  );
};

export default ASNList;